import type { AnalyticsEventType, Locale } from '@nk/shared';
import { measurementAllowed, onConsentChange } from '@/lib/consent';

/**
 * The client tracker — phases.md Phase 16.
 *
 * Phase 9 left a stub here that recorded nothing behind an `ANALYTICS_ENABLED` flag and
 * carried the *rules* instead: one view per article per session, sent after the reader
 * has actually stayed. Those rules survive below; what is new is that there is now an
 * endpoint to send them to, and five more things worth measuring.
 *
 * Four properties this file exists to guarantee:
 *
 * - **It never blocks the page.** Events go into a queue and leave in batches, on a
 *   timer, on page hide, and on nothing else. No measurement is ever awaited by
 *   rendering.
 * - **It never loses the last one.** The interesting event — how long someone read, how
 *   far they scrolled — happens as they leave, which is exactly when `fetch` is killed.
 *   `sendBeacon` is what survives that, and `visibilitychange` (not `unload`, which iOS
 *   Safari never fires) is when it runs.
 * - **It never sends an identifier.** There is no visitor id in this file because there
 *   is nowhere to put one: the payload has no field for it and the server derives its
 *   own rotating hash. See `analytics-privacy.ts` on the API side.
 * - **It stops when asked.** A reader who declines mid-session stops being measured
 *   immediately, and the queue they had accumulated is thrown away rather than flushed.
 */

const ENDPOINT = '/api/v1/public/events';

/** Big enough that a scroll to the bottom is one request, small enough to never be lost. */
const MAX_QUEUE = 20;
/** A quiet page flushes on this timer; a busy one flushes when it fills the queue. */
const FLUSH_INTERVAL_MS = 5_000;

interface QueuedEvent {
  type: AnalyticsEventType;
  path: string;
  articleId?: number;
  videoId?: number;
  value?: number;
  meta?: Record<string, string | number | boolean>;
}

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;
let locale: Locale = 'as';
let started = false;

/**
 * Called once from the app shell. Wires the flush triggers and the consent listener;
 * everything else in this module is a no-op until it has run, which is also what keeps
 * the tracker out of the server render.
 */
export function startTracker(activeLocale: Locale): void {
  locale = activeLocale;
  if (started || typeof window === 'undefined') return;
  started = true;

  // `visibilitychange` fires on a tab switch, an app switch and a navigation away, on
  // every platform. `pagehide` covers the back/forward cache, where the page is frozen
  // rather than unloaded and `visibilitychange` may already have fired.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);

  onConsentChange((state) => {
    // A refusal drops what was queued. Sending it "because we already collected it"
    // would make the choice retroactive in the one direction that cannot be undone.
    if (state === 'declined') queue = [];
  });
}

export function setTrackerLocale(next: Locale): void {
  locale = next;
}

/** Queues an event. The only way anything reaches the endpoint. */
export function track(event: QueuedEvent): void {
  if (typeof window === 'undefined' || !measurementAllowed()) return;

  queue.push(event);
  if (queue.length >= MAX_QUEUE) {
    flush();
    return;
  }
  if (timer === undefined) {
    timer = setTimeout(flush, FLUSH_INTERVAL_MS);
  }
}

/**
 * Sends whatever is queued, preferring `sendBeacon`.
 *
 * The queue is emptied *before* the send, not after: a beacon that fails is one lost
 * batch, while a queue that is only cleared on success grows without bound on a page
 * whose requests are being blocked by an extension — and then sends the lot at once if
 * one ever gets through.
 */
export function flush(): void {
  if (timer !== undefined) {
    clearTimeout(timer);
    timer = undefined;
  }
  if (queue.length === 0 || !measurementAllowed()) {
    queue = [];
    return;
  }

  const body = JSON.stringify({
    locale,
    // The document referrer, not the `Referer` header: on a beacon the header is this
    // page, which would make every reader look like internal traffic.
    referrer: document.referrer || undefined,
    events: queue,
  });
  queue = [];

  try {
    if (navigator.sendBeacon) {
      // A Blob with an explicit type, because a bare string is sent as `text/plain` and
      // Express's JSON parser would not touch it.
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      return;
    }
  } catch {
    // Some browsers throw from `sendBeacon` when a queue limit is hit. Fall through.
  }

  void fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => undefined);
}

// --- The rules ---------------------------------------------------------------

/** One view per article per session (Phase 9's rule, kept). */
const SESSION_KEY = 'nk:viewed';

function alreadyCounted(articleId: number): boolean {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    const seen = raw ? (JSON.parse(raw) as number[]) : [];
    if (seen.includes(articleId)) return true;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify([...seen, articleId].slice(-200)));
    return false;
  } catch {
    // Private browsing, blocked storage: count the view rather than lose it, and accept
    // the duplicate. An analytics nicety must never throw inside a page render.
    return false;
  }
}

/** Long enough that a reader who bounced off a wrong link is not counted as a view. */
const VIEW_DELAY_MS = 3_000;

export function trackPageView(path: string): void {
  track({ type: 'page_view', path });
}

/**
 * Returns a cleanup function, so a reader who navigates away inside the delay is not
 * counted — `useEffect` handles that automatically.
 */
export function trackArticleView(articleId: number, path: string): () => void {
  if (alreadyCounted(articleId)) return () => undefined;

  const handle = setTimeout(() => track({ type: 'article_view', path, articleId }), VIEW_DELAY_MS);
  return () => clearTimeout(handle);
}

export function trackShare(articleId: number, path: string, network: string): void {
  track({ type: 'share_click', path, articleId, meta: { network } });
}

/**
 * A search a reader ran. Logged here as well as server-side because the two answer
 * different questions: the server sees every query the API was asked, including a
 * crawler's; this sees the ones a person typed into the box.
 */
export function trackSearch(path: string, query: string, resultCount: number): void {
  track({ type: 'search', path, value: resultCount, meta: { query: query.slice(0, 200) } });
}

export function trackVideoPlay(videoId: number, path: string): void {
  track({ type: 'video_play', path, videoId });
}

/**
 * Starts a stopwatch on mount and reports on unmount — so the reader navigating away is
 * what ends the measurement, and a page nobody watched reports nothing.
 *
 * Watch time is measured against the wall clock rather than the player, because the
 * player is a cross-origin iframe: we can see that a reader pressed play and when they
 * left, and we deliberately do not ask YouTube's API for more than that. It is an upper
 * bound on attention, which is what the video report calls it.
 */
export function trackVideoWatchTime(
  videoId: number,
  path: string,
  durationSeconds: number | null,
): () => void {
  const started = Date.now();

  return () => {
    const seconds = Math.round((Date.now() - started) / 1000);
    // Under three seconds is a reader who opened the wrong link, not a view.
    if (seconds < 3) return;

    track({ type: 'video_watch', path, videoId, value: Math.min(seconds, 86_400) });
    // "Completion" against the clip's own length, capped: a reader who left the tab
    // open on a two-minute video for an hour did not watch it thirty times.
    if (durationSeconds && seconds >= durationSeconds * 0.9) {
      track({ type: 'video_complete', path, videoId });
    }
    flush();
  };
}

/**
 * PRD §3.1 lists bookmarking as "if enabled", and phases.md puts persistence in the
 * post-MVP backlog. The button exists behind this flag so the article header's layout is
 * the final one; turning it on is a flag plus a Phase 19 endpoint.
 */
export const BOOKMARKS_ENABLED = false;
